import { Fragment, Suspense, lazy, useEffect, useState, type ReactNode } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api, type LineageSku, type ProductPageResult } from '../api/client.js';
import { AnalysisBanner } from '../components/AnalysisBanner.js';
import {
  AdjacentShipmentsNote,
  Item,
  LEDGER_EVENTS_SHOWN,
  LeadTimeLinesTable,
  LedgerCard,
  MonthlyHistoryCard,
  PlanRowFacts,
  SellThroughCard,
  StockoutsCard,
  hasAdjacentShipments,
} from '../components/DetailSections.js';
import {
  HistoryReading,
  KindBadge,
  RemovalReading,
  VerdictBadge,
} from '../components/DuplicateBadges.js';
import { OffersCard } from '../components/OffersCard.js';
import { ProductThumb } from '../components/ProductThumb.js';
import { SkuStatus } from '../components/SkuStatus.js';
import { WhenVisible } from '../components/WhenVisible.js';
import {
  Alert,
  Card,
  PageHeader,
  Spinner,
  StockNote,
  TableScroll,
  Tabs,
} from '../components/ui.js';
import { day, decimal, integer } from '../format.js';
import { familyPath } from '../lib/families.js';
import {
  defaultSkuTab,
  lineageSummary,
  productBreadcrumb,
  productNotes,
  variantFacts,
} from '../lib/product-page.js';
import { COVER_BAND_LABEL, COVER_TINT, productPagePath } from '../lib/products.js';
import { listingIssueText, retiredNote, taxonomyLinks } from '../lib/sku-taxonomy.js';

const EmbeddedSalesStock = lazy(() => import('../charts/EmbeddedSalesStock.js'));

/**
 * One product, everything about it in one place. The product is the CHILD ASIN:
 * every seller SKU on it is the same product (a barcode switch mints a new SKU on
 * the same ASIN; the app never deletes the old one, so both carry history). The
 * page reads ONE payload (`GET /api/products/:asin`, or `/api/products/sku/:id` for
 * an ASIN-less SKU) plus the lazy chart and the offers card — no per-row requests.
 *
 * Sections, in order: header, key numbers, sales and stock chart, the lineage (the
 * SKUs; each links to its SKU page, where the per-SKU planning inputs stay), the
 * plan block, competition, lead times, sell-through and stockouts (per SKU), the 24
 * months and the ledger, and the owner's notes. See docs/products.md, "The product
 * page".
 */
export function ProductPage() {
  const { asin, skuId } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const target = asin !== undefined ? asin : `sku:${skuId ?? ''}`;

  const query = useQuery({
    queryKey: ['product-page', target],
    queryFn: () =>
      api.get<ProductPageResult>(
        asin !== undefined
          ? `/api/products/${encodeURIComponent(asin)}`
          : `/api/products/sku/${encodeURIComponent(skuId ?? '')}`,
      ),
  });

  // A SKU that has an ASIN opens its ASIN's product: move to that address, handing
  // the payload over so the page does not fetch it twice.
  const redirectAsin = asin === undefined ? (query.data?.asin ?? null) : null;
  useEffect(() => {
    if (redirectAsin === null || !query.data) return;
    queryClient.setQueryData(['product-page', redirectAsin], query.data);
    void navigate(productPagePath({ asin: redirectAsin, skuId: 0 }), { replace: true });
  }, [redirectAsin, query.data, queryClient, navigate]);

  if (query.isPending) return <Spinner label="Loading product" />;
  if (query.isError) return <Alert>{query.error.message}</Alert>;
  // Keyed by product, so moving to another product resets the per-SKU tab state.
  return <ProductView key={query.data.productKey} data={query.data} />;
}

function ProductView({ data }: { data: ProductPageResult }) {
  const { product, lineage, catalog } = data;
  const live = lineage.find((s) => s.live) ?? null;
  const title = catalog?.title ?? live?.title ?? lineage[0]?.title ?? null;

  return (
    <div className="space-y-4">
      <Breadcrumb data={data} />
      <Header data={data} title={title} />
      <AnalysisBanner />
      <KeyNumbers data={data} />

      <Card className="p-4">
        <WhenVisible>
          <Suspense fallback={<Spinner label="Loading chart" />}>
            {product.asin ? (
              <EmbeddedSalesStock scope="asin" chartKey={product.asin} />
            ) : (
              <EmbeddedSalesStock scope="sku" chartKey={String(product.skuId)} />
            )}
          </Suspense>
        </WhenVisible>
      </Card>

      <LineageCard data={data} />
      <PlanBlock data={data} />
      {product.asin && <OffersCard asin={product.asin} />}
      <LeadTimesBlock data={data} />
      <SellThroughBlock data={data} />
      <MonthlyHistoryCard months={data.history.months} title="Last 24 months (all SKUs)" />
      <LedgerCard ledger={data.ledger} shown={LEDGER_EVENTS_SHOWN} showSku={lineage.length > 1} />
      <NotesBlock lineage={lineage} />
    </div>
  );
}

function Breadcrumb({ data }: { data: ProductPageResult }) {
  const crumbs = productBreadcrumb(data.taxonomy);
  return (
    <nav aria-label="Breadcrumb" className="text-xs text-muted">
      {crumbs.map((c, i) => (
        <Fragment key={c.to}>
          {i > 0 && <span aria-hidden> › </span>}
          <Link to={c.to} className="hover:underline">
            {c.label}
          </Link>
        </Fragment>
      ))}
      <span aria-hidden> › </span>
      <span className="text-body">{data.product.name}</span>
    </nav>
  );
}

function Chip({ tone, children, title }: { tone: string; children: ReactNode; title?: string }) {
  return (
    <span
      className={`inline-flex items-center rounded px-1.5 py-0.5 text-xs font-medium ring-1 ring-inset ${tone}`}
      title={title}
    >
      {children}
    </span>
  );
}

const TONE = {
  ok: 'bg-ok-bg text-ok-text ring-ok-line',
  warn: 'bg-warn-bg text-warn-text ring-warn-line',
  muted: 'bg-sunken text-muted ring-line',
};

function Header({ data, title }: { data: ProductPageResult; title: string | null }) {
  const { product, taxonomy, lineage, catalog } = data;
  const live = lineage.find((s) => s.live) ?? null;
  const links = taxonomyLinks(taxonomy);
  const issue = listingIssueText(taxonomy);
  const retired = retiredNote(taxonomy);

  return (
    <div className="flex flex-col gap-4 sm:flex-row sm:items-start">
      <ProductThumb asin={product.asin} title={product.name} size={120} />
      <div className="min-w-0 flex-1 space-y-2">
        <PageHeader
          title={product.name}
          {...(title && title !== product.name ? { description: title } : {})}
        />
        <div className="flex flex-wrap items-center gap-2">
          {taxonomy.live ? (
            <Chip tone={TONE.ok}>Live</Chip>
          ) : (
            <Chip tone={TONE.muted} title="None of its SKUs is Active">
              Retired
            </Chip>
          )}
          {data.duplicateKind && <KindBadge kind={data.duplicateKind} />}
          {links.review && (
            <Link to={links.review} className="text-xs text-brand-600 hover:underline">
              <Chip tone={TONE.warn}>Needs review</Chip>
            </Link>
          )}
          <span className="text-xs text-muted">{lineageSummary(lineage)}</span>
        </div>
        <dl className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm sm:grid-cols-4">
          {variantFacts(taxonomy).map((f) => (
            <Item key={f.label} label={f.label} value={f.value} />
          ))}
          <Item
            label="ASIN"
            value={<span className="font-mono text-xs">{product.asin ?? 'None'}</span>}
          />
          <Item
            label="Live SKU"
            value={
              live ? (
                <Link to={`/skus/${live.id}`} className="text-brand-600 hover:underline">
                  {live.sellerSku}
                </Link>
              ) : (
                'None Active'
              )
            }
          />
          {product.family && (
            <Item
              label="Family"
              value={
                <Link
                  to={familyPath(product.family.key)}
                  className="text-brand-600 hover:underline"
                >
                  {product.family.name || product.family.key}
                </Link>
              }
            />
          )}
          {catalog?.brand && <Item label="Brand" value={catalog.brand} />}
          {catalog?.productType && <Item label="Amazon product type" value={catalog.productType} />}
          {catalog?.parentAsin && (
            <Item
              label="Parent ASIN"
              value={<span className="font-mono text-xs">{catalog.parentAsin}</span>}
            />
          )}
        </dl>
        {issue && <Alert tone="warning">{issue}</Alert>}
        {retired && <p className="text-xs text-muted">{retired}</p>}
      </div>
    </div>
  );
}

/** One number in the key-numbers row. */
function Stat({
  label,
  value,
  hint,
  tint,
}: {
  label: string;
  value: string;
  hint?: string;
  tint?: string;
}) {
  return (
    <div className={`rounded-md px-3 py-2 ring-1 ring-inset ${tint ?? 'bg-surface ring-line'}`}>
      <div className="text-xs text-muted">{label}</div>
      <div className="tabular text-lg font-semibold text-strong">{value}</div>
      {hint && <div className="text-[11px] text-muted">{hint}</div>}
    </div>
  );
}

function KeyNumbers({ data }: { data: ProductPageResult }) {
  const { product, history } = data;
  return (
    <section aria-label="Key numbers" className="space-y-2">
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4 lg:grid-cols-8">
        <Stat label="On hand" value={integer(product.onHand)} />
        <Stat label="Inbound" value={integer(product.inbound)} />
        <Stat label="Units 30" value={integer(product.units30)} />
        <Stat label="Units 90" value={integer(product.units90)} />
        <Stat label="Units 365" value={integer(product.units365)} />
        <Stat
          label="Velocity / 30"
          value={decimal(product.velocityPer30, 1)}
          hint="One rate across the SKUs: in stock, else active months"
        />
        <Stat
          label="Days of cover on hand"
          value={product.daysOfCover === null ? '—' : decimal(product.daysOfCover, 0)}
          hint={COVER_BAND_LABEL[product.coverBand]}
          tint={COVER_TINT[product.coverBand]}
        />
        <Stat
          label="Score"
          value={data.score === null ? '—' : decimal(data.score, 2)}
          hint="The live SKU's restock score"
        />
      </div>
      <p className="text-xs">
        <StockNote source={history.stockSource} asOf={history.stockAsOf} /> · Last sale{' '}
        {day(product.lastSaleDate)} · Summed across every SKU on the ASIN.
      </p>
    </section>
  );
}

function LineageCard({ data }: { data: ProductPageResult }) {
  const { lineage, coverage, duplicateKind } = data;
  const showRemoval = duplicateKind !== null || lineage.some((s) => s.delistedOn !== null);
  return (
    <Card testId="product-lineage">
      <div className="flex flex-wrap items-baseline justify-between gap-2 border-b border-line px-4 py-2">
        <h2 className="text-sm font-medium text-body">Lineage</h2>
        <span className="text-xs text-muted">{lineageSummary(lineage)}</span>
      </div>
      <TableScroll>
        <table className="w-full text-sm">
          <thead className="border-b border-line text-left text-xs text-muted">
            <tr>
              <th className="px-3 py-2 font-medium">Seller SKU</th>
              <th className="px-3 py-2 font-medium">Status</th>
              <th className="px-3 py-2 font-medium">First seen</th>
              <th className="px-3 py-2 font-medium">Last seen</th>
              <th className="px-3 py-2 font-medium">First sale</th>
              <th className="px-3 py-2 font-medium">Last sale</th>
              <th className="px-3 py-2 text-right font-medium">U30</th>
              <th className="px-3 py-2 text-right font-medium">U90</th>
              <th className="px-3 py-2 text-right font-medium">U365</th>
              <th className="px-3 py-2 text-right font-medium">Life</th>
              <th className="px-3 py-2 text-right font-medium">On hand</th>
              <th className="px-3 py-2 text-right font-medium">Inbound</th>
              <th className="px-3 py-2 text-right font-medium">Reserved</th>
              <th className="px-3 py-2 font-medium">On the plan</th>
              <th className="px-3 py-2 font-medium">History</th>
              {showRemoval && <th className="px-3 py-2 font-medium">Ready to remove</th>}
              <th className="px-3 py-2 font-medium">Planning</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-line">
            {lineage.map((s) => (
              <LineageRow key={s.id} sku={s} showRemoval={showRemoval} />
            ))}
          </tbody>
        </table>
      </TableScroll>
      <p className="border-t border-line px-4 py-2 text-xs text-muted">
        Every seller SKU on this ASIN is this one product: a barcode switch mints a new SKU on the
        same ASIN, and the app never deletes the old one, so each keeps its own history and the
        figures above add them up. The live SKU is the Active one the plan shows (most units, then
        latest sale). History captured means a SKU&apos;s sales fall inside the imported order
        coverage ({coverageText(coverage.order)}) and ledger coverage (
        {coverageText(coverage.ledger)}). Case pack, minimum, lead time, unit cost, and the plan
        override stay per SKU: edit them on the SKU page.
      </p>
    </Card>
  );
}

/** A coverage window as "from–to", or "none loaded" before any data. */
function coverageText(c: { from: string | null; to: string | null }): string {
  return c.from && c.to ? `${day(c.from)}–${day(c.to)}` : 'none loaded';
}

function LineageRow({ sku: s, showRemoval }: { sku: LineageSku; showRemoval: boolean }) {
  return (
    <tr>
      <td className="whitespace-nowrap px-3 py-1.5">
        <span className="inline-flex items-center gap-2">
          <Link to={`/skus/${s.id}`} className="text-brand-600 hover:underline">
            {s.sellerSku}
          </Link>
          {s.live && <Chip tone={TONE.ok}>Live</Chip>}
        </span>
      </td>
      <td className="whitespace-nowrap px-3 py-1.5 text-body">
        <SkuStatus status={s.status} delistedOn={s.delistedOn} listingStatus={s.listingStatus} />
      </td>
      <td className="whitespace-nowrap px-3 py-1.5">{day(s.firstSeenDate)}</td>
      <td className="whitespace-nowrap px-3 py-1.5">{day(s.lastSeenDate)}</td>
      <td className="whitespace-nowrap px-3 py-1.5">{day(s.firstSaleDate)}</td>
      <td className="whitespace-nowrap px-3 py-1.5">{day(s.lastSaleDate)}</td>
      <td className="tabular px-3 py-1.5 text-right">{integer(s.units30)}</td>
      <td className="tabular px-3 py-1.5 text-right">{integer(s.units90)}</td>
      <td className="tabular px-3 py-1.5 text-right">{integer(s.units365)}</td>
      <td className="tabular px-3 py-1.5 text-right">{integer(s.unitsLifetime)}</td>
      <td className="tabular px-3 py-1.5 text-right">{integer(s.onHand)}</td>
      <td className="tabular px-3 py-1.5 text-right">{integer(s.inbound)}</td>
      <td className="tabular px-3 py-1.5 text-right">{integer(s.reserved)}</td>
      <td className="whitespace-nowrap px-3 py-1.5">
        <VerdictBadge hidden={s.duplicateHidden} reason={s.hideReason} />
      </td>
      <td className="whitespace-nowrap px-3 py-1.5">
        <HistoryReading captured={s.historyCaptured} reason={s.historyReason} />
      </td>
      {showRemoval && (
        <td className="whitespace-nowrap px-3 py-1.5">
          <RemovalReading delistedOn={s.delistedOn} readyToRemove={s.readyToRemove} />
        </td>
      )}
      <td className="whitespace-nowrap px-3 py-1.5">
        <Link to={`/skus/${s.id}`} className="text-xs text-brand-600 hover:underline">
          Edit on SKU page
        </Link>
      </td>
    </tr>
  );
}

function PlanBlock({ data }: { data: ProductPageResult }) {
  const { plan, lineage } = data;
  const skuById = new Map(lineage.map((s) => [s.id, s]));
  return (
    <Card className="p-5" testId="product-plan">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="text-sm font-medium text-body">Purchase plan</h2>
        <div className="flex items-baseline gap-3 text-xs text-muted">
          <span>
            {plan.current
              ? `${plan.current.name}${plan.current.status === 'sent' ? ' (sent)' : ''}`
              : 'No plan yet: figures on the default settings'}
          </span>
          <Link to="/purchase-plan" className="text-brand-600 hover:underline">
            Open the plan
          </Link>
        </div>
      </div>
      {!plan.available ? (
        <p className="mt-2 text-sm text-muted" role="status" data-testid="product-plan-unavailable">
          {plan.unavailableReason === 'timeout'
            ? 'The plan figures are not available right now: computing the plan took too long. '
            : 'The plan figures are not available right now: the plan could not be computed. '}
          The rest of this page is complete. Reload to try again, or open the plan.
        </p>
      ) : plan.rows.length === 0 ? (
        <p className="mt-2 text-sm text-muted">No plan row for this product&apos;s SKUs.</p>
      ) : (
        <div className="divide-y divide-line">
          {plan.rows.map((row) => {
            const s = skuById.get(row.id);
            return (
              <section key={row.id} aria-label={`Plan line ${row.sellerSku}`} className="py-3">
                <div className="flex flex-wrap items-center gap-2 text-sm">
                  <Link
                    to={`/skus/${row.id}`}
                    className="font-medium text-brand-600 hover:underline"
                  >
                    {row.sellerSku}
                  </Link>
                  {s?.live && <Chip tone={TONE.ok}>Live</Chip>}
                  {row.duplicateHidden && (
                    <Chip
                      tone={TONE.warn}
                      title="A zero-stock duplicate: the plan hides it and leaves it out of the budget"
                    >
                      Hidden on the plan
                    </Chip>
                  )}
                </div>
                <PlanRowFacts row={row} />
              </section>
            );
          })}
        </div>
      )}
    </Card>
  );
}

function LeadTimesBlock({ data }: { data: ProductPageResult }) {
  const { stats, bySku, lines, openInboundUnits } = data.leadTimes;
  return (
    <Card>
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-line px-4 py-2">
        <h2 className="text-sm font-medium text-body">Lead times</h2>
        {stats.n > 0 ? (
          <span className="text-xs text-muted">
            Median {integer(stats.medianTotal)}d · p80 {integer(stats.p80Total)}d over{' '}
            {integer(stats.n)} {stats.n === 1 ? 'order' : 'orders'}
            {stats.suggestedLeadTimeDays !== null &&
              ` · suggested ${integer(stats.suggestedLeadTimeDays)}d`}
            {openInboundUnits > 0 && ` · ${integer(openInboundUnits)} units still inbound`}
          </span>
        ) : null}
      </div>
      {bySku.length > 1 && (
        <TableScroll>
          <table className="w-full text-sm">
            <thead className="border-b border-line text-left text-xs text-muted">
              <tr>
                <th className="px-3 py-2 font-medium">SKU</th>
                <th className="px-3 py-2 text-right font-medium">Orders</th>
                <th className="px-3 py-2 text-right font-medium">Median</th>
                <th className="px-3 py-2 text-right font-medium">p80</th>
                <th className="px-3 py-2 text-right font-medium">Open inbound</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-line">
              {bySku.map((b) => (
                <tr key={b.sellerSku}>
                  <td className="whitespace-nowrap px-3 py-1.5 font-mono text-xs">{b.sellerSku}</td>
                  <td className="tabular px-3 py-1.5 text-right">{integer(b.n)}</td>
                  <td className="tabular px-3 py-1.5 text-right">
                    {b.medianTotal === null ? '—' : `${integer(b.medianTotal)}d`}
                  </td>
                  <td className="tabular px-3 py-1.5 text-right">
                    {b.p80Total === null ? '—' : `${integer(b.p80Total)}d`}
                  </td>
                  <td className="tabular px-3 py-1.5 text-right">{integer(b.openInboundUnits)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </TableScroll>
      )}
      {hasAdjacentShipments(lines) && <AdjacentShipmentsNote />}
      {lines.length === 0 ? (
        <p className="px-4 py-3 text-sm text-muted">
          No sent plan lines for this product yet. Once an order is marked sent and a shipment and
          receipt land, its measured lead time shows here.
        </p>
      ) : (
        <LeadTimeLinesTable lines={lines} showSku={data.lineage.length > 1} />
      )}
    </Card>
  );
}

/**
 * Sell-through and stockouts per SKU: the derived daily tables are per seller SKU,
 * so the page shows each SKU's own reading (a tab per SKU) rather than inventing a
 * product-level figure.
 */
function SellThroughBlock({ data }: { data: ProductPageResult }) {
  const { sellThrough, liveSkuId } = data;
  const [active, setActive] = useState<number | null>(() => defaultSkuTab(sellThrough, liveSkuId));
  const current = sellThrough.find((s) => s.skuId === active) ?? sellThrough[0];
  if (!current) return null;
  const multiple = sellThrough.length > 1;
  return (
    <section aria-label="Sell-through and stockouts" className="space-y-2">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-sm font-medium text-body">Sell-through and stockouts</h2>
        {multiple && (
          <span className="text-xs text-muted">
            Per SKU: the daily stock tables are kept per seller SKU.
          </span>
        )}
      </div>
      {multiple && (
        <Tabs
          items={sellThrough.map((s) => ({
            id: String(s.skuId),
            label: s.skuId === liveSkuId ? `${s.sellerSku} (live)` : s.sellerSku,
          }))}
          active={String(current.skuId)}
          onChange={(id) => setActive(Number(id))}
        />
      )}
      <div role={multiple ? 'tabpanel' : undefined} className="space-y-4">
        <SellThroughCard
          detail={current.detail}
          title={multiple ? `Sell-through · ${current.sellerSku}` : 'Sell-through'}
        />
        <StockoutsCard
          detail={current.detail}
          title={multiple ? `Stockouts · ${current.sellerSku}` : 'Stockouts'}
        />
      </div>
    </section>
  );
}

/**
 * The owner's notes. Notes are per SKU today, so the live SKU's lead (labeled),
 * with any note on another SKU of the product listed beneath; editing stays on the
 * SKU page.
 */
function NotesBlock({ lineage }: { lineage: LineageSku[] }) {
  const { lead, leadReason, others } = productNotes(lineage);
  if (!lead) return null;
  return (
    <Card className="p-5">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="text-sm font-medium text-body">Notes</h2>
        <Link to={`/skus/${lead.id}`} className="text-xs text-brand-600 hover:underline">
          Edit on the SKU page
        </Link>
      </div>
      <p className="mt-1 text-xs text-muted">
        Notes on {lead.sellerSku} ({leadReason === 'live' ? 'the live SKU' : 'the newest listing'}
        ). Notes are kept per SKU.
      </p>
      <p className="mt-2 whitespace-pre-wrap text-sm text-body">{lead.notes ?? '—'}</p>
      {others.length > 0 && (
        <dl className="mt-3 space-y-2 border-t border-line pt-3">
          {others.map((s) => (
            <div key={s.id}>
              <dt className="text-xs text-muted">
                On{' '}
                <Link to={`/skus/${s.id}`} className="text-brand-600 hover:underline">
                  {s.sellerSku}
                </Link>
              </dt>
              <dd className="whitespace-pre-wrap text-sm text-body">{s.notes}</dd>
            </div>
          ))}
        </dl>
      )}
    </Card>
  );
}
